import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'

import { appListenerMiddleware } from 'reduxTyped'
import { AiConversationsRateSocketEventsSchemas } from 'apps/chat/interfaces/aiConversationsRateShema'
import { AiConversationsRateState } from 'apps/chat/models/chatInterfaces'
import { messagesSlice } from 'apps/chat/reducers/messagesSlice'
import { Ability } from 'common/core/constants/Ability'
import { getAbility } from 'common/core/selectors/abilitiesSelectors'
import { getIsAccountFlagEnabled } from 'common/core/selectors/appSelectors'
import { getUserRole } from 'common/userRoles'
import { UserRoles } from 'common/userRoles/userRolesInterfaces'
import {
  AiConversationFeedbackApi,
  AiConversationFeedbackApiNew,
} from 'shared/api/requests/aiConversationFeedback'
import {
  AiConversationRate,
  FeedbackCategory,
} from 'shared/api/requests/aiConversationFeedback/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

import { setCurrentThread } from '../actions/threadsActions'
import { ChatMessageType } from '../types/chatMessageTypeList'
import { ChatMessageInterface } from '../types/chatMessageTypes'

export const NAMESPACE = 'aiConversationsRate'

export const initialState: AiConversationsRateState = {
  conversations: {},
}

const { reducer, actions } = createSlice({
  initialState,
  name: NAMESPACE,
  reducers: {
    aiConversationsRateReset: (state) => {
      state.conversations = {}
    },
    aiConversationsRateUpdated: (
      state,
      action: PayloadAction<Record<string, AiConversationRate>>,
    ) => {
      state.conversations = {
        ...state.conversations,
        ...action.payload,
      }
    },
  },
})

const retrieveConversationsStatuses = createAsyncAction(
  `${NAMESPACE}/retrieveConversationsStatuses`,
  async (sessionIds: string[], { dispatch, getState }) => {
    const proxyEnabled = getIsAccountFlagEnabled(
      getState(),
      AccountFlag.ENABLE_PROXY_CONVERSATION_FEEDBACK,
    )

    const { data } = proxyEnabled
      ? await AiConversationFeedbackApiNew.retrieve({
          query: { session_ids: sessionIds },
        })
      : await AiConversationFeedbackApi.retrieve({
          body: { session_ids: sessionIds },
        })

    if (data.data !== null) {
      dispatch(actions.aiConversationsRateUpdated(data.data))
    }
  },
  {
    condition: (sessionIds: string[]) => sessionIds.length > 0,
  },
)

const saveConversationRate = createAsyncAction(
  `${NAMESPACE}/saveConversationRate`,
  async (
    {
      sessionId,
      isPositive,
      category,
      comment,
    }: {
      sessionId: string
      isPositive: boolean
      category: FeedbackCategory | null
      comment: string | null
    },
    { dispatch, getState },
  ) => {
    const proxyEnabled = getIsAccountFlagEnabled(
      getState(),
      AccountFlag.ENABLE_PROXY_CONVERSATION_FEEDBACK,
    )

    proxyEnabled
      ? await AiConversationFeedbackApiNew.save({
          body: { session_id: sessionId, is_positive: isPositive, category, comment },
          query: { client_id: getClientId() },
        })
      : await AiConversationFeedbackApi.save({
          body: { data: { session_id: sessionId, is_positive: isPositive, category, comment } },
          query: { client_id: getClientId() },
        })

    dispatch(
      actions.aiConversationsRateUpdated({
        [sessionId]: {
          session_id: sessionId,
          is_positive: isPositive,
        },
      }),
    )
  },
)

const messagesSliceUpdatedMatchers = [
  messagesSlice.actions.getListFx.fulfilled,
  messagesSlice.actions.getNextListFx.fulfilled,
  messagesSlice.actions.getPrevListFx.fulfilled,
] as const

appListenerMiddleware.startListening({
  matcher: isAnyOf<[...typeof messagesSliceUpdatedMatchers]>(...messagesSliceUpdatedMatchers),
  effect: (action, { dispatch, getState }) => {
    const aiAlwaysOnAbility = getAbility(getState(), Ability.AI_ALWAYS_ON)
    const userRole = getUserRole(getState())

    if (!aiAlwaysOnAbility.isEnabled || ![UserRoles.ADMIN, UserRoles.EDITOR].includes(userRole)) {
      return
    }

    if (action.payload && 'messages' in action.payload) {
      const messages = action.payload.messages as ChatMessageInterface[]
      const conversationsIds = messages.reduce((prev, cur) => {
        const isFinished = cur.type === ChatMessageType.AI_AUTOMATION_INSTAGRAM_FINISHED
        const hasId = 'model' in cur && cur.model && 'session_id' in cur.model

        if (isFinished && hasId) {
          cur.model.session_id && prev.push(cur.model.session_id)
        }

        return prev
      }, [] as string[])

      dispatch(retrieveConversationsStatuses(conversationsIds))
    }
  },
})

appListenerMiddleware.startListening({
  actionCreator: setCurrentThread,
  effect: (action, { dispatch }) => {
    if (action.payload === null) {
      dispatch(actions.aiConversationsRateReset())
    }
  },
})

anotherTabNotificationsListener.on(
  AiConversationsRateSocketEventsSchemas.feedbackLeft,
  (payload, dispatch) => {
    dispatch(
      actions.aiConversationsRateUpdated({
        [payload.data.session_id]: payload.data,
      }),
    )
  },
)

export const AiConversationsRateActions = {
  setConversationStatus: saveConversationRate,
}

export const AiConversationsRateReducer = reducer
